import React, { useMemo } from 'react';
import classnames from 'classnames';
import Gallery from 'react-photo-gallery';

import MediaCard from 'components/Globals/MediaCard';
import GallerySpaceFiller from 'components/Globals/Media/GallerySpaceFiller';

import { TRANSFORMATIONS } from 'components/uiLibrary/Image';

import useCurrentWidth from 'utils/hooks/useCurrentWidth';
import withLayoutEffect from 'utils/hocs/withLayoutEffect';
import withLightbox from 'utils/hocs/withLightbox';
import { getGalleryMediaDimensions, regroupMedia, processMediaList } from 'utils/media';

import classes from './MasonryView.module.scss';

const MediaComponent = ({ key, photo, margin, direction, left, top, index, onClick }) => {
  const { src, height, width, isFiller, fillerText, media, isPortrait, mediaContent } = photo;
  const { source_info, credit, copyright } = media || {};

  let style = {};

  if (direction === 'column') {
    style = {
      ...style,
      position: 'absolute',
      left,
      top,
      ...(index === 2 ? { marginTop: margin * 2 } : {}),
    };
  }

  const imageProps = {
    src,
    height,
    transformations: isPortrait ? TRANSFORMATIONS.GALLERY_PORTRAIT_VIEW : TRANSFORMATIONS.GALLERY_LANDSCAPE_VIEW,
    alt: media?.title || mediaContent?.title,
    blurHash: media?.blurHash,
    useIntersectionObserver: true,
    disableNextImage: true,
    onClick: () => onClick(media?.id, { index }),
  };

  return (
    <div
      key={`${key}-${index}`}
      className={classnames(classes.wrapper, { [classes.isFiller]: isFiller })}
      style={{ margin, height, width, ...style }}
    >
      {isFiller ? (
        <GallerySpaceFiller
          text={fillerText}
          image={src}
          transformations={isPortrait ? TRANSFORMATIONS.GALLERY_PORTRAIT_VIEW : TRANSFORMATIONS.GALLERY_LANDSCAPE_VIEW}
        />
      ) : (
        <MediaCard
          data={media}
          title={media?.title}
          imageProps={imageProps}
          attribution={{ source_info, credit, copyright }}
        />
      )}
    </div>
  );
};

const MasonryUnit = withLayoutEffect(
  ({ photos, direction = 'row', fillerText, openLightbox, addFillerMedia = true }) => {
    const width = useCurrentWidth();

    const items = useMemo(() => {
      if (addFillerMedia && width > 768 && photos?.length === 1) {
        const photo = photos[0];

        const isPortrait = photo?.isPortrait;
        const fillerMedia = {
          ...photo,
          ...getGalleryMediaDimensions(!isPortrait),
          isPortrait: !isPortrait,
          fillerText,
          isFiller: true,
        };

        return [photo, fillerMedia];
      }

      return photos;
    }, [photos, fillerText, width, addFillerMedia]);

    if (photos?.length === 0) {
      return null;
    }

    return (
      <Gallery
        photos={items}
        margin={4}
        direction={direction}
        columns={width <= 600 ? 1 : 2}
        renderImage={MediaComponent}
        onClick={openLightbox}
      />
    );
  },
);

const MasonryView = ({ data, fillerText, showLightbox, mediaContent, addFillerMedia }) => {
  const mediaSet = useMemo(() => regroupMedia(processMediaList(data, mediaContent)), [data, mediaContent]);

  const openLightbox = mediaId => {
    showLightbox({ data, mediaId, mediaContent });
  };

  return (
    <div className={classes.masonryView}>
      {mediaSet?.map(({ direction, items }, index) => (
        <MasonryUnit
          key={index}
          photos={items}
          mediaContent={mediaContent}
          direction={direction}
          fillerText={fillerText}
          openLightbox={openLightbox}
          addFillerMedia={addFillerMedia}
        />
      ))}
    </div>
  );
};

export default withLightbox(MasonryView);
